import React, { useCallback, useState } from "react";
import { defineMessages, FormattedMessage } from "react-intl";
import { defaultFormMessages } from "../shared/crud/form";
import { defaultListMessages } from "../shared/crud/list";
import styled from "styled-components";
import { SectionHeading } from "../../components/section-heading";
import { Alert, Button, Card } from "react-bootstrap";
import { useDropzone } from "react-dropzone";
import axios from "http/customAxios";
import { FullWidthLoader } from "../../components/full-width-loader";
import { useConfig } from "../../lib/config";
import ListGroup from "react-bootstrap/ListGroup";

export interface Props {
  propertyTicker: string;
}
interface OperationError {
  message: string;
}

export interface Cancellation {
  propertyTicker: string;
  reservationId: string;
  cancellationResultStatus: string;
}

export interface BatchResponse {
  cancellations: Cancellation[];
}
const messages = {
  ...defaultFormMessages,
  ...defaultListMessages,
  ...defineMessages({
    title: {
      id: "menu.reservations.batchReservationCancellation",
      defaultMessage: "Batch reservation cancellation",
    },
    description: {
      id: "batchReservationCancellation.description",
      defaultMessage:
        "Provide a csv files with only one column, listing all the ids (PXXXXXXX) that you want to cancel",
    },
    dropHere: {
      id: "app.imageManager.dropHere",
      defaultMessage: "Drop files here...",
    },
    summary: {
      id: "multiUpdateInventoryResultRenderer.summary",
      defaultMessage: "Result summary",
    },
  }),
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  margin: 1rem;
  > * {
    margin: 1rem 0;
  }
`;
const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  margin: 1rem;
  > * {
    margin: 0 5px;
  }
  :last-child {
    margin-right: 5px;
  }
`;

const DropzoneContainer = styled.div`
  width: auto;
  height: 100px;
  border: 2px dashed #dae6e8;
  background-color: ${({ theme }) => theme.greyBackground};
  font-size: 21px;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  margin-bottom: 25px;
  outline: none;
`;

const ResultContainer = styled.div`
  margin: 16px;
`;

function BatchReservationCancellation({ propertyTicker }: Props) {
  const config = useConfig();
  const [file, setFile] = useState<File | undefined>(undefined);
  const [isLoading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<OperationError | null | undefined>(undefined);
  const [result, setResult] = useState<BatchResponse | undefined>(undefined);
  const onDrop = useCallback(<T extends File>(acceptedFiles: T[]) => {
    if (!acceptedFiles || acceptedFiles.length === 0) {
      return;
    }
    setFile(acceptedFiles[0]);
  }, []);
  const { getRootProps, getInputProps } = useDropzone({ onDrop });
  const handleImport = useCallback(async () => {
    if (!file) {
      return;
    }
    const formData = new FormData();
    formData.append("file", file);
    setLoading(true);
    setError(undefined);
    try {
      // TODO: replace with httpclient
      const response = await axios.delete(
        `${config.apiEndpoint}/api/properties/bulk/${propertyTicker}/cancel/reservations`,
        {
          headers: {
            "content-type": "multipart/form-data",
          },
          data: formData,
        }
      );
      setResult(response.data);
    } catch (err) {
      setError({ message: "Access denied" });
    }
    setLoading(false);
  }, [config.apiEndpoint, file, propertyTicker]);

  if (isLoading) {
    return <FullWidthLoader />;
  }

  return (
    <>
      <SectionHeading>
        <FormattedMessage {...messages.title} />
      </SectionHeading>
      <Card className="ml-4 mr-4 mb-4 mt-4">
        <Container>
          <div>
            <FormattedMessage {...messages.description} />
          </div>
          <DropzoneContainer {...getRootProps()}>
            <input {...getInputProps()} />
            {file ? <div>{file.name}</div> : <FormattedMessage {...messages.dropHere} />}
          </DropzoneContainer>
        </Container>
        {error && (
          <Alert variant="danger">
            <strong>
              <FormattedMessage {...messages.saveError} />
            </strong>
          </Alert>
        )}
        <ButtonContainer>
          <Button variant="primary" type="button" onClick={handleImport}>
            <FormattedMessage {...messages.import} />
          </Button>
        </ButtonContainer>
        {result && (
          <ResultContainer>
            <Alert variant="success">
              <strong>
                <FormattedMessage {...messages.saveSuccess} />
              </strong>
            </Alert>
            <Card>
              <Card.Header as="h5">
                <FormattedMessage {...messages.summary} />
              </Card.Header>
              <ListGroup variant="flush">
                {(result.cancellations || []).map(c => (
                  <ListGroup.Item>
                    {c.reservationId}: {c.cancellationResultStatus}
                  </ListGroup.Item>
                ))}
              </ListGroup>
            </Card>
          </ResultContainer>
        )}
      </Card>
    </>
  );
}

export default BatchReservationCancellation;
